<template>
  <div class="main-layout">
    <!-- Sidebar Menu -->
    <SidebarMenu
      ref="sidebar"
      @toggle-menu="handleSidebarToggle"
      @disconnect="handleLogout"
    />

    <!-- Top Navbar -->
    <TopNavbar
      :page-title="$route.meta.title"
      :user-name="userName"
      :role-label="roleLabel"
      :breadcrumb-section="breadcrumbSection"
      :is-sidebar-collapsed="isSidebarCollapsed"
      @logout="handleLogout"
      @toggle-sidebar="$refs.sidebar.toggleMenu()"
    />

    <!-- Main Content Area -->
    <div
      class="main-content"
      :class="[
        isSidebarCollapsed ? 'sidebar-collapsed' : '',
        $route.meta?.bgClass || '',
      ]"
    >
      <router-view />
    </div>
  </div>
</template>

<script>
import SidebarMenu from './SidebarMenu.vue';
import TopNavbar from './TopNavbar.vue';
import { mapActions, mapGetters } from 'vuex';
import { displayName } from '@/shared/utils/user-display';

const ROLE_LABELS = {
  SuperAdmin: 'Super administrateur',
  Admin: 'Administrateur',
  Professional: 'Professionnel',
};

// Route name -> the section it belongs to, so a form keeps its parent's name.
const CRUMB_SECTIONS = {
  dashboard: 'Tableau de bord',
  'pro-dashboard': 'Tableau de bord',
  'list-users': 'Utilisateurs',
  'create-user': 'Utilisateurs',
  'update-user': 'Utilisateurs',
  surveys: 'Enquêtes',
  'my-surveys': 'Enquêtes',
  companies: 'Entreprises',
  'company-details': 'Entreprises',
  'survey-questions': 'Questions',
};

export default {
  name: 'MainLayout',
  components: {
    SidebarMenu,
    TopNavbar,
  },
  props: {
    pageTitle: {
      type: String,
      default: 'Tableau de Bord Global',
    },
  },
  data() {
    return {
      isSidebarCollapsed: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['user', 'userRole']),
    userName() {
      return displayName(this.user);
    },

    roleLabel() {
      return ROLE_LABELS[this.userRole] || '';
    },

    /**
     * Second half of the breadcrumb. Sub-screens report their section, so
     * "Ajouter un utilisateur" still reads as Administration / Utilisateurs.
     */
    breadcrumbSection() {
      const name = this.$route.name;
      return CRUMB_SECTIONS[name] || this.$route.meta?.title || '';
    },
  },
  methods: {
    ...mapActions('auth', ['logoutLocal']),
    handleSidebarToggle(isCollapsed) {
      this.isSidebarCollapsed = isCollapsed;
      console.log('Sidebar toggled, collapsed:', isCollapsed);
    },
    async handleLogout() {
      try {
        // Clear localStorage and Vuex state
        await this.logoutLocal();
        // Redirect to login page
        this.$router.push({ name: 'login' });
      } catch (error) {
        console.error('Error during logout:', error);
        // Even if there's an error, try to redirect to login
        this.$router.push({ name: 'login' });
      }
    },
  },
};
</script>

<style scoped>
.main-layout {
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.main-content {
  margin-left: 248px;
  margin-top: 72px;
  height: calc(100vh - 72px);
  overflow-y: auto;
  /* Prevent horizontal scrolling caused by the sidebar + full-width content */
  overflow-x: hidden;
  padding-left: 2rem;
  padding-right: 2rem;
  box-sizing: border-box;
  /* Make width account for the left sidebar so content won't overflow the viewport */
  transition: margin-left 0.18s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 76px;
  width: calc(100% - 76px);
}
/* Tablette: le rail garde sa largeur, seules les marges se resserrent. */
@media (max-width: 991px) {
  .main-content {
    padding-left: 1.2rem;
    padding-right: 1.2rem;
  }
}

/* Telephone: le rail passe en tiroir, le contenu occupe toute la largeur. */
@media (max-width: 767px) {
  .main-content,
  .main-content.sidebar-collapsed {
    margin-left: 0;
    width: 100%;
    padding-left: 0.9rem;
    padding-right: 0.9rem;
  }
}

.bg-gray {
  background-color: #f9fbfb;
}
</style>
